import { useState } from "react";
import { AlertTriangle, ExternalLink, Pencil, Plus } from "lucide-react";
import type { WorkerCertification } from "@crewledger/shared/schemas";
import { CommandCenterCard, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  CERTIFICATION_TYPE_LABELS,
  createWorkerCertification,
  updateWorkerCertification,
} from "../certifications-api";
import { CertificationFormModal } from "./certification-form-modal";

const STATUS_STYLES: Record<
  WorkerCertification["status"],
  string
> = {
  ACTIVE: "bg-green-50 text-green-700 border-green-200",
  EXPIRED: "bg-red-50 text-red-700 border-red-200",
  PENDING: "bg-amber-50 text-amber-800 border-amber-200",
};

interface WorkerCertificationsSectionProps {
  workerId: string;
  canEdit: boolean;
  certifications: WorkerCertification[];
  onUpdated: () => void;
}

export function WorkerCertificationsSection({
  workerId,
  canEdit,
  certifications,
  onUpdated,
}: WorkerCertificationsSectionProps) {
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WorkerCertification | null>(null);
  const [saving, setSaving] = useState(false);

  const expiredCount = certifications.filter((c) => c.isExpired).length;
  const expiringSoonCount = certifications.filter((c) => c.expiresSoon && !c.isExpired).length;

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (cert: WorkerCertification) => {
    setEditing(cert);
    setModalOpen(true);
  };

  const handleSubmit = async (values: Parameters<typeof createWorkerCertification>[1]) => {
    setSaving(true);
    try {
      if (editing) {
        await updateWorkerCertification(editing.id, values);
        toast({ variant: "success", title: "Certification updated" });
      } else {
        await createWorkerCertification(workerId, values);
        toast({ variant: "success", title: "Certification added" });
      }
      setModalOpen(false);
      onUpdated();
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to save certification",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CommandCenterCard
        title="Certifications"
        description={
          expiredCount > 0
            ? `${expiredCount} expired certification${expiredCount === 1 ? "" : "s"} on file`
            : expiringSoonCount > 0
              ? `${expiringSoonCount} expiring within 30 days`
              : undefined
        }
        variant={expiredCount > 0 ? "alert" : expiringSoonCount > 0 ? "default" : "default"}
        action={
          canEdit ? (
            <Button variant="outline" size="sm" onClick={openCreate}>
              <Plus className="mr-1.5 h-4 w-4" />
              Add
            </Button>
          ) : undefined
        }
      >
        {expiredCount > 0 ? (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              This worker has expired certifications. They may not operate equipment that requires
              valid credentials unless a manager overrides on daily entry.
            </p>
          </div>
        ) : null}

        {certifications.length === 0 ? (
          <p className="text-sm text-muted-foreground">No certifications on file.</p>
        ) : (
          <ul className="space-y-2">
            {certifications.map((cert) => (
              <li
                key={cert.id}
                className={cn(
                  "rounded-md border px-3 py-3",
                  cert.isExpired && "border-red-200 bg-red-50/40",
                  cert.expiresSoon && !cert.isExpired && "border-amber-200 bg-amber-50/40",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-navy-deep">{cert.certificationName}</p>
                      <span
                        className={cn(
                          "inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize",
                          STATUS_STYLES[cert.status],
                        )}
                      >
                        {cert.status.toLowerCase()}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {CERTIFICATION_TYPE_LABELS[cert.certificationType]}
                      {cert.certificationCode ? ` · ${cert.certificationCode}` : ""}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                      {cert.issuedAt ? <span>Issued {cert.issuedAt}</span> : null}
                      {cert.expiresAt ? (
                        <span className={cert.isExpired ? "font-semibold text-red-700" : undefined}>
                          {cert.isExpired ? "Expired" : "Expires"} {cert.expiresAt}
                        </span>
                      ) : null}
                    </div>
                    {cert.notes ? (
                      <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">
                        {cert.notes}
                      </p>
                    ) : null}
                    {cert.documentUrl ? (
                      <a
                        href={cert.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-orange-construction hover:underline"
                      >
                        View document
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                  </div>
                  {canEdit ? (
                    <Button variant="ghost" size="sm" onClick={() => { openEdit(cert); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CommandCenterCard>

      <CertificationFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); }}
        onSubmit={handleSubmit}
        initial={editing}
        loading={saving}
      />
    </>
  );
}
